import React, { useEffect, useMemo, useState } from 'react';
import { Cookie, X } from 'lucide-react';

import { DEFAULT_LOCALE } from '../i18n/config';

const translations = {
  en: {
    title: 'Cookie settings',
    description:
      'We use cookies to provide core functionality and remember your preferences, including language settings.',
    essentialTitle: 'Essential cookies',
    essentialDescription: 'Required for core website functionality and security.',
    functionalTitle: 'Functional cookies',
    functionalDescription: 'Store your preferences, including selected language.',
    analyticsTitle: 'Analytics cookies',
    analyticsDescription: 'Help us understand how visitors use the website.',
    acceptAll: 'Accept all',
    acceptEssential: 'Essential only',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    dismiss: 'Dismiss cookie banner',
  },
  de: {
    title: 'Cookie-Einstellungen',
    description:
      'Wir verwenden Cookies für die Grundfunktionen der Website und um Ihre Präferenzen, einschließlich der Sprache, zu speichern.',
    essentialTitle: 'Notwendige Cookies',
    essentialDescription: 'Erforderlich für Kernfunktionen und Sicherheit der Website.',
    functionalTitle: 'Funktionale Cookies',
    functionalDescription: 'Speichern Ihre Präferenzen, einschließlich der gewählten Sprache.',
    analyticsTitle: 'Analyse-Cookies',
    analyticsDescription: 'Helfen uns zu verstehen, wie Besucher die Website nutzen.',
    acceptAll: 'Alle akzeptieren',
    acceptEssential: 'Nur notwendige',
    showDetails: 'Details anzeigen',
    hideDetails: 'Details ausblenden',
    dismiss: 'Cookie-Banner schließen',
  },
  it: {
    title: 'Impostazioni cookie',
    description:
      'Utilizziamo i cookie per garantire le funzioni essenziali del sito e memorizzare le preferenze, inclusa la lingua.',
    essentialTitle: 'Cookie essenziali',
    essentialDescription: 'Necessari per le funzioni principali e la sicurezza del sito.',
    functionalTitle: 'Cookie funzionali',
    functionalDescription: 'Memorizzano le preferenze, inclusa la lingua selezionata.',
    analyticsTitle: 'Cookie analitici',
    analyticsDescription: 'Ci aiutano a capire come viene utilizzato il sito.',
    acceptAll: 'Accetta tutti',
    acceptEssential: 'Solo essenziali',
    showDetails: 'Mostra dettagli',
    hideDetails: 'Nascondi dettagli',
    dismiss: 'Chiudi banner cookie',
  },
  es: {
    title: 'Configuración de cookies',
    description:
      'Usamos cookies para garantizar funciones esenciales del sitio y guardar preferencias, incluido el idioma.',
    essentialTitle: 'Cookies esenciales',
    essentialDescription: 'Necesarias para las funciones principales y la seguridad del sitio.',
    functionalTitle: 'Cookies funcionales',
    functionalDescription: 'Guardan tus preferencias, incluido el idioma seleccionado.',
    analyticsTitle: 'Cookies de analítica',
    analyticsDescription: 'Nos ayudan a comprender cómo se utiliza el sitio.',
    acceptAll: 'Aceptar todo',
    acceptEssential: 'Solo esenciales',
    showDetails: 'Mostrar detalles',
    hideDetails: 'Ocultar detalles',
    dismiss: 'Cerrar banner de cookies',
  },
};

const CookieBanner = ({ locale = DEFAULT_LOCALE }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const t = useMemo(() => translations[locale] ?? translations[DEFAULT_LOCALE], [locale]);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    setIsVisible(false);
  };

  const acceptEssential = () => {
    localStorage.setItem('cookieConsent', 'essential');
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-700 bg-slate-950/95 p-4 backdrop-blur-xl">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-start gap-4">
          <Cookie className="mt-1 h-7 w-7 flex-shrink-0 text-amber-300" />

          <div className="flex-1">
            <h3 className="text-base font-semibold text-white sm:text-lg">{t.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{t.description}</p>

            {showDetails && (
              <div className="mt-4 space-y-3 rounded-lg border border-slate-800 bg-slate-900/70 p-4 text-sm">
                <div>
                  <h4 className="font-semibold text-white">{t.essentialTitle}</h4>
                  <p className="mt-1 text-slate-400">{t.essentialDescription}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-white">{t.functionalTitle}</h4>
                  <p className="mt-1 text-slate-400">{t.functionalDescription}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-white">{t.analyticsTitle}</h4>
                  <p className="mt-1 text-slate-400">{t.analyticsDescription}</p>
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={acceptAll}
                className="rounded-md bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-white"
              >
                {t.acceptAll}
              </button>
              <button
                onClick={acceptEssential}
                className="rounded-md border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-slate-400 hover:bg-slate-900"
              >
                {t.acceptEssential}
              </button>
              <button
                onClick={() => setShowDetails((previousValue) => !previousValue)}
                className="text-sm font-semibold text-slate-300 transition-colors hover:text-white"
              >
                {showDetails ? t.hideDetails : t.showDetails}
              </button>
            </div>
          </div>

          <button
            onClick={() => setIsVisible(false)}
            aria-label={t.dismiss}
            className="rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-900 hover:text-slate-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
